/*------------------------------------------------------------------------------
   About      : Edit an existing insight (epic DA-697, II-FE-3)

                **Two tabs over one documentId**, as on the reco edit screen:
                the editable copy (Draft or Modified, depending on `editStatus`)
                and the Published version, read-only. The published side comes
                from `publishedById`, which the slice keeps un-merged for
                exactly this — `mergeInsightPartitions` collapses a record
                present in both partitions down to the modified one, so the live
                version is unreachable from `insights` alone.

                Switching tabs with unsaved edits is confirmed, not silent: the
                form re-seeds from the other version and the edit would be gone.

                Loads the record out of the merged list in the store, falling
                back to a fetch when the user arrives by deep link with no list
                loaded. Previous/Next walk the SAME filtered order the list
                screen shows, so paging back from here lands where the author
                expects.

                Saving goes through `buildInsightPatch`, which carries only
                changed fields and omits `code`, `insightId` and `editStatus` by
                construction — those are server-owned and quill rejects a
                client-supplied value outright.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions,
         DialogContent, DialogContentText, DialogTitle, Stack, Tab, Tabs,
         Typography, useTheme } from '@mui/material'
import ArrowBackIcon        from '@mui/icons-material/ArrowBack'
import ChevronLeftIcon      from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon     from '@mui/icons-material/ChevronRight'
import { useSelector }      from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import { useGlobalConfig }             from 'context/GlobalContext'
import { ANALYTICS_EVENTS }            from 'framework/analytics/AnalyticsEvents'
import { useAppDispatch }              from 'store/Store'
import { selectCMSSelectedPilot,
         selectCMSSelectedPilotDocId } from 'store/dataReducers/cmsPilotSlice'
import { CONTENT_EDIT_STATUS }         from 'types/cms/frontendTypes'

import { InsightApis }        from '../api/insightAPI'
import { useInsightCatalog }  from '../hooks/useInsightCatalog'
import { useInsightWorkflowActions } from '../hooks/useInsightWorkflowActions'
import { InsightEditForm }    from '../components/InsightEditForm'
import { InsightWorkflowControls } from '../components/InsightWorkflowControls'
import { TemplateCriteriaBuilder } from '../components/TemplateCriteriaBuilder'
import { TagRecommendationsPanel } from '../components/TagRecommendationsPanel'
import { InsightCreateData }  from '../types/insightTypes'
import type { BuiltValidationState } from '../utils/insightValidation'
import { buildInsightPatch,
         isNoOpPatch,
         toEditableData }     from '../utils/insightDiff'
import { CriteriaCondition,
         conditionsToCriteria,
         criteriaToConditions } from '../utils/criteriaBuilder'
import { filterInsights }     from '../utils/insightFilters'
import { selectInsightFilters,
         selectInsights,
         selectPublishedById,
         selectStatusLabels,
         setInsightsFromPartitions,
         setReviewWorkflow,
         upsertInsight }      from '../store/insightUISlice'

export const InsightEditPage = () => {

  const { rc }     = useGlobalConfig()
  const theme      = useTheme()
  const dispatch   = useAppDispatch()
  const navigate   = useNavigate()
  const { id }     = useParams<{ id : string }>()

  const insights           = useSelector(selectInsights)
  const filters            = useSelector(selectInsightFilters)
  const publishedById      = useSelector(selectPublishedById)
  const statusLabels       = useSelector(selectStatusLabels)
  const reviewWorkflow     = useSelector((s: any) => s.insightUI?.reviewWorkflow ?? null)
  const selectedPilotDocId = useSelector(selectCMSSelectedPilotDocId)
  const selectedPilot      = useSelector(selectCMSSelectedPilot)
  const pilotId            = selectedPilot?.pilotId

  const { catalog } = useInsightCatalog(rc)
  const workflow    = useInsightWorkflowActions(selectedPilotDocId, pilotId)

  const record    = insights.find((i) => i.documentId === id)
  const published = id ? publishedById[id] : undefined

  const [data, setData]           = useState<InsightCreateData | null>(null)
  const [isSaving, setIsSaving]   = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [saveError, setSaveError] = useState<string | undefined>()

  /* Criteria are edited as rows, not as the stored map. `unparsed` holds any
     predicate the builder cannot model, so a save preserves it rather than
     silently dropping a criterion that governs when the insight fires. */
  const [conditions, setConditions] = useState<CriteriaCondition[]>([])
  const [unparsedCriteria, setUnparsedCriteria] = useState<Record<string, string>>({})

  /* Draft / Published tabs, as on the reco edit screen. Tab 0 is the editable
     copy; tab 1 is whatever production is serving, read-only. Both describe the
     same documentId — they are two versions of one record, not two records. */
  const [activeTab, setActiveTab]   = useState(0)
  const [pendingTab, setPendingTab] = useState<number | null>(null)

  // Deep-link fallback: the list may never have been visited, so there is
  // nothing in the store to read. There is no get-one endpoint in the contract,
  // so this refetches the pilot and lets the store index it.
  const ensureLoaded = useCallback(async () => {
    if (record || !selectedPilotDocId) return
    setIsLoading(true)
    const resp = await InsightApis.getInsights(rc, selectedPilotDocId)
    if (resp.data) dispatch(setInsightsFromPartitions(resp.data))
    setIsLoading(false)
  }, [record, selectedPilotDocId, rc, dispatch])

  useEffect(() => { ensureLoaded() }, [ensureLoaded])

  // Seed form state from the record once, and re-seed when the id changes so
  // Previous/Next loads the next record rather than carrying edits across.
  useEffect(() => {
    if (record) {
      setData(toEditableData(record))
      const exploded = criteriaToConditions(record.templateCriteria, catalog?.criteriaGrammar)
      setConditions(exploded.conditions)
      setUnparsedCriteria(exploded.unparsed)
    }
    setSaveError(undefined)
    // Previous/Next lands on a different record; leaving the Published tab
    // selected would show the new record's live copy while the header reads
    // like an edit screen.
    setActiveTab(0)
    workflow.clearOutcome()
  }, [record?.documentId]) // eslint-disable-line react-hooks/exhaustive-deps

  // The workflow table is fetched by the list screen; on a deep link straight
  // to edit it has never loaded, and without it the publish action cannot be
  // resolved from `actions[currentStatus]`.
  useEffect(() => {
    if (reviewWorkflow) return
    let cancelled = false
    InsightApis.getReviewWorkflow(rc).then((resp) => {
      if (!cancelled && resp.data) dispatch(setReviewWorkflow(resp.data))
    })
    return () => { cancelled = true }
  }, [reviewWorkflow, rc, dispatch])

  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.EDITOR_LOADED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* Previous/Next walk the same filtered, ordered set the list shows — using
     the raw store order instead would jump to a record the author cannot see
     in their current view. */
  const orderedIds = useMemo(
    () => filterInsights(insights, filters, catalog).map((i) => i.documentId),
    [insights, filters, catalog],
  )
  const position = id ? orderedIds.indexOf(id) : -1
  const prevId   = position > 0 ? orderedIds[position - 1] : undefined
  const nextId   = position >= 0 && position < orderedIds.length - 1 ? orderedIds[position + 1] : undefined

  /* Criteria rows are folded into the form data before diffing, so a criteria
     edit makes the record dirty and reaches `templateCriteria` in the payload.
     Sent whole because the server MERGES this blob rather than replacing it —
     a partial object would leave removed conditions alive. */
  const { criteria : builtCriteria, duplicateVariables } = useMemo(
    () => conditionsToCriteria(conditions, unparsedCriteria, catalog?.criteriaGrammar),
    [conditions, unparsedCriteria, catalog?.criteriaGrammar],
  )

  const dataWithCriteria = useMemo<InsightCreateData | null>(
    () => (data ? { ...data, templateCriteria : builtCriteria } : null),
    [data, builtCriteria],
  )

  /* The three validation slots are client-owned: quill stores what we send,
     verbatim, and replaces the object wholesale. Folded in at save time rather
     than held in form state, because they describe the text being saved — and
     because sending a stale object would overwrite good slots with old ones. */
  const payloadFor = (validation?: BuiltValidationState): InsightCreateData | null =>
    dataWithCriteria && validation
      ? { ...dataWithCriteria,
          validationState : validation.validationState,
          lastValidation  : validation.lastValidation }
      : dataWithCriteria

  const patch = useMemo(
    () => (record && dataWithCriteria
      ? buildInsightPatch(record.documentId, dataWithCriteria, record)
      : undefined),
    [record, dataWithCriteria],
  )
  const isDirty = !!patch && !isNoOpPatch(patch)

  /* ── The published tab ───────────────────────────────────────────────────
     Derived, never held in state: it is a reference copy with no edit path, so
     form state for it would only be something that could drift from the store.
     `publishedById` keeps the `published` partition un-merged precisely so this
     version survives `mergeInsightPartitions` collapsing the two. */
  const publishedData = useMemo(
    () => (published ? toEditableData(published) : undefined),
    [published],
  )

  const publishedConditions = useMemo(
    () => criteriaToConditions(published?.templateCriteria, catalog?.criteriaGrammar),
    [published?.templateCriteria, catalog?.criteriaGrammar],
  )

  /* Switching tabs re-renders the form from a different record, so pending
     edits would vanish. Confirmed rather than silently dropped — the same
     guard, and the same wording, as the reco editor's tab switch. */
  const requestTab = (next: number) => {
    if (next === activeTab) return
    if (isDirty) {
      setPendingTab(next)
      return
    }
    setActiveTab(next)
  }

  const save = async (validation?: BuiltValidationState) => {
    if (!record || !data || !selectedPilotDocId || !pilotId) return

    const payload = payloadFor(validation)
    if (!payload) return
    const patch = buildInsightPatch(record.documentId, payload, record)

    // An unchanged row must not be sent: it would bump a cache timestamp and
    // demote PUBLISHED → MODIFIED for no user-visible reason.
    if (isNoOpPatch(patch)) return

    // Two rows for one variable means the stored map silently keeps only the
    // last. Block rather than losing a criterion behind another.
    if (duplicateVariables.length) {
      setSaveError(
        `${duplicateVariables.join(', ')} is used by more than one criteria condition. ` +
        'Criteria are stored one predicate per variable — use a range instead.',
      )
      return
    }

    setIsSaving(true)
    setSaveError(undefined)
    rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.EDITOR_SAVE_CLICKED)

    const resp = await InsightApis.updateAll(rc, {
      pilotId,
      data    : [patch],
    })

    setIsSaving(false)

    if (resp.error) {
      setSaveError(resp.error.message || 'Failed to save')
      rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.EDITOR_SAVE_FAILED)
      return
    }
    // A 200 can still carry per-item failures; one bad row does not fail the
    // batch, so an unexamined success here would silently lose the edit.
    if (resp.data?.error?.length) {
      setSaveError(
        resp.data.error
          .flatMap((entry) => Object.entries(entry).map(([key, message]) => `${key}: ${message}`))
          .join('; '),
      )
      rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.EDITOR_SAVE_FAILED)
      return
    }

    const saved = resp.data?.insights?.find((i) => i.documentId === record.documentId)
    if (saved) {
      // Re-seed from the server's version: it owns editStatus (PUBLISHED →
      // MODIFIED auto-demotion) and allowedTransitions, both of which change here.
      dispatch(upsertInsight(saved))
      setData(toEditableData(saved))
      const exploded = criteriaToConditions(saved.templateCriteria, catalog?.criteriaGrammar)
      setConditions(exploded.conditions)
      setUnparsedCriteria(exploded.unparsed)
    }
    rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.EDITOR_SAVED)
  }

  const resetToRecord = () => {
    if (!record) return
    setData(toEditableData(record))
    const exploded = criteriaToConditions(record.templateCriteria, catalog?.criteriaGrammar)
    setConditions(exploded.conditions)
    setUnparsedCriteria(exploded.unparsed)
    setSaveError(undefined)
  }

  if (isLoading) {
    return (
      <Box sx={{ p : 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading insight…</Typography>
        </Stack>
      </Box>
    )
  }

  if (!record || !data) {
    return (
      <Box sx={{ p : 3 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/cms/interesting-insight/list')}>
          Back to insights
        </Button>
        <Alert severity="error" sx={{ mt : 2 }}>
          {selectedPilotDocId
            ? 'This insight was not found in the selected project. It may have been discarded, or it may belong to a different project.'
            : 'Select a project to load this insight.'}
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p : 3 }}>
      <Stack direction="row" alignItems="center" sx={{ mb : 2 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/cms/interesting-insight/list')}>
          Back to insights
        </Button>

        <Box sx={{ flexGrow : 1 }} />

        <Stack direction="row" spacing={1}>
          <Button
            size="small" variant="outlined" startIcon={<ChevronLeftIcon />}
            disabled={!prevId || isDirty}
            onClick={() => prevId && navigate(`/cms/interesting-insight/edit/${prevId}`)}
          >
            Previous
          </Button>
          <Button
            size="small" variant="outlined" endIcon={<ChevronRightIcon />}
            disabled={!nextId || isDirty}
            onClick={() => nextId && navigate(`/cms/interesting-insight/edit/${nextId}`)}
          >
            Next
          </Button>
        </Stack>
      </Stack>

      {/* Navigating away with edits pending would drop them without warning,
          so the controls are disabled rather than silently discarding. */}
      {isDirty && (prevId || nextId) && (
        <Alert severity="info" sx={{ mb : 2 }}>
          Save or undo your changes to move between insights.
        </Alert>
      )}

      {/* ── Version tabs ───────────────────────────────────────────────────
          Two versions of ONE documentId. Published is disabled when the record
          has never been published — there is no live version to show, and an
          enabled tab onto an empty form reads as a load failure. */}
      <Box sx={{ borderBottom : 1, borderColor : 'divider', mb : 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_event, next) => requestTab(next)}
          textColor="inherit"
          slotProps={{ indicator : { sx : { backgroundColor : theme.palette.text.secondary } } }}
          sx={{ '& .Mui-selected' : { color : theme.palette.text.secondary } }}
        >
          <Tab label={record.editStatus === CONTENT_EDIT_STATUS.MODIFIED ? 'Modified' : 'Draft'} />
          <Tab
            label="Published"
            disabled={!published}
          />
        </Tabs>
      </Box>

      {activeTab === 1 && published && publishedData ? (
        <InsightEditForm
          mode="edit"
          readOnly
          value={publishedData}
          /* Nothing here can fire it — every control is disabled or absent —
             but a writable handler would make that a promise rather than a
             property of the component. */
          onChange={() => undefined}
          record={published}
          pilotId={pilotId}
          catalog={catalog}
          isSaving={false}
          isDirty={false}
          onSaveDraft={() => undefined}
          onSaveSubmit={() => undefined}
          criteriaSlot={(
            <TemplateCriteriaBuilder
              conditions={publishedConditions.conditions}
              onChange={() => undefined}
              unparsed={publishedConditions.unparsed}
              catalog={catalog}
              insightType={published.type}
              templateString={published.templateString ?? ''}
              disabled
            />
          )}
          relatedSlot={selectedPilotDocId ? (
            <TagRecommendationsPanel
              insight={published}
              utilityDocumentId={selectedPilotDocId}
              readOnly
            />
          ) : undefined}
        />
      ) : (
      <InsightEditForm
        mode="edit"
        /* The criteria-folded copy, not raw `data`: the builder owns
           templateCriteria, and the form's modified-field highlight and its
           capping check both have to see the live value rather than the one
           last read off the record. */
        value={dataWithCriteria ?? data}
        onChange={(patchData) => setData((prev) => ({ ...(prev ?? {}), ...patchData }))}
        record={record}
        published={published}
        pilotId={pilotId}
        catalog={catalog}
        isSaving={isSaving}
        isDirty={isDirty}
        saveError={saveError}
        onSaveDraft={save}
        onSaveSubmit={save}
        onUndo={resetToRecord}
        onDiscard={async () => {
          const outcome = await workflow.discardDrafts([record.documentId])
          // A never-published draft is DELETED rather than reverted, so there is
          // no record left to show — go back to the list instead of rendering
          // a not-found state.
          if (outcome?.kind !== 'FAILURE' && !published) {
            navigate('/cms/interesting-insight/list')
          }
        }}
        workflowSlot={(
          <InsightWorkflowControls
            insight={record}
            reviewWorkflow={reviewWorkflow}
            statusLabels={statusLabels}
            isRunning={workflow.isRunning}
            outcome={workflow.outcome}
            /* A workflow move re-reads the record server-side, so pending edits
               would be lost. Blocked rather than silently discarded. */
            disabled={isDirty}
            onChangeStatus={(next) => workflow.changeQaStatus(record.documentId, next)}
            onPublish={() => workflow.publish([record.documentId])}
            onUnpublish={() => workflow.unpublish([record.documentId])}
            onBlocked={(reason) => {
              setSaveError(reason)
              rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.QA_TRANSITION_BLOCKED)
            }}
          />
        )}
        criteriaSlot={(
          <TemplateCriteriaBuilder
            conditions={conditions}
            onChange={setConditions}
            unparsed={unparsedCriteria}
            catalog={catalog}
            insightType={data.type}
            templateString={data.templateString ?? ''}
            disabled={isSaving}
          />
        )}
        relatedSlot={selectedPilotDocId ? (
          <TagRecommendationsPanel
            insight={record}
            utilityDocumentId={selectedPilotDocId}
            pilotId={pilotId}
            /* The response carries a fresh allowedTransitions — push it into
               the store so the QA dropdown above re-renders from it. */
            onTagged={(updated) => dispatch(upsertInsight(updated))}
            disabled={isSaving}
          />
        ) : undefined}
      />
      )}

      {/* Switching tabs re-seeds the form from the other version, so an
          unsaved edit would be gone with no way back. */}
      <Dialog open={pendingTab !== null} onClose={() => setPendingTab(null)}>
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This insight has changes you have not saved. Switching versions discards
            them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingTab(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingTab !== null) setActiveTab(pendingTab)
              setPendingTab(null)
              // Back to the stored record, so returning to the draft tab does
              // not show edits the user was told were discarded.
              resetToRecord()
            }}
          >
            Discard and switch
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default InsightEditPage
